import { useRouter } from 'vue-router'

/**
 * Returns a `@click` handler for containers that render wiki markdown.
 * Anchors rewritten by `createWikiMarkdown` carry `data-wiki-link="true"`
 * so the full-page navigation that would otherwise fire on a `/p/...`
 * href is swapped for a `router.push` that stays inside the SPA.
 *
 * Why a composable: both `HomeView` (article pane) and `ThreadDrawer`
 * (assistant bubbles) render wiki markdown and need the same
 * interception logic. The only asymmetry is that the drawer wants to
 * close itself after navigation, which callers express via
 * `onNavigate`.
 *
 * Matches browser link-activation semantics — modifier-click, right-
 * click, and default-prevented events all fall through to the browser
 * so the user's explicit intent (new tab, new window) is respected.
 */
export function useInternalLinkClicks(onNavigate?: () => void): (event: MouseEvent) => void {
  const router = useRouter()
  return (event: MouseEvent): void => {
    if (event.defaultPrevented || event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    const anchor = (event.target as HTMLElement | null)?.closest('a[data-wiki-link="true"]') as HTMLAnchorElement | null
    if (!anchor) return

    const href = anchor.getAttribute('href')
    if (!href) return

    event.preventDefault()
    router.push(href)
    onNavigate?.()
  }
}
